import { useState } from "react";
import { Badge, Button, Card, ChipGroup } from "@bfc-software/pallet";

import {
  exceptionCounts,
  formatAge,
  visibleExceptions,
  type ExceptionFilter,
  type ShiftException,
} from "../-data/dashboard-model";
import type { DashboardT } from "./dashboard-t";
import styles from "./dashboard.module.css";

/**
 * What needs a manager's eye this shift, sorted by how long it has waited.
 *
 * Sample data, and badged as such here rather than in the page header: it is the
 * only part of the dashboard that is (its implementation is deferred).
 *
 * The row actions are inert: Reassign needs a path for a *suspended* task (the
 * assignment endpoint only moves assigned-but-unclaimed ones), and no endpoint
 * marks a manual validation done. They are drawn so the design is whole, and
 * disabled rather than hidden so nobody wonders where they went.
 */
export function ExceptionsPanel({
  exceptions,
  t,
}: {
  exceptions: ReadonlyArray<ShiftException>;
  t: DashboardT;
}) {
  const [filter, setFilter] = useState<ExceptionFilter>("actionable");
  const counts = exceptionCounts(exceptions);
  const rows = visibleExceptions(exceptions, filter);

  return (
    <Card
      variant="panel"
      layout="body-only"
      data-test-role="dashboard-exceptions"
      body={
        <div className={styles.exceptions}>
          <div className={styles.panelHead}>
            <span className={styles.panelTitleRow}>
              <span className={styles.panelTitle}>
                {t("dashboard.exceptions.title")}
              </span>
              <Badge
                label={t("dashboard.mockedNotice")}
                color={{ mode: "static", tone: "purple" }}
              />
            </span>
            <span className={styles.panelHint}>
              {t("dashboard.exceptions.hint")}
            </span>
          </div>
          <ChipGroup<ExceptionFilter>
            ariaLabel={t("dashboard.exceptions.ariaLabel")}
            selected={filter}
            onSelect={setFilter}
            chips={[
              {
                key: "actionable",
                label: t("dashboard.exceptions.actionable"),
                count: counts.actionable,
              },
              {
                key: "informational",
                label: t("dashboard.exceptions.informational"),
                count: counts.informational,
              },
              {
                key: "all",
                label: t("dashboard.exceptions.all"),
                count: counts.all,
              },
            ]}
          />
          {rows.length === 0 ? (
            <p className={styles.panelHint}>
              {t("dashboard.exceptions.empty")}
            </p>
          ) : (
            <ul className={styles.exceptionList}>
              {rows.map((e) => (
                <li
                  key={e.id}
                  className={styles.exception}
                  data-exception={e.id}
                >
                  <div className={styles.exceptionBody}>
                    <span className={styles.exceptionTitle}>
                      {t(`dashboard.exceptions.kind.${e.kind}`)}
                      <Badge
                        label={t(`dashboard.exceptions.severity.${e.severity}`)}
                        color={{
                          mode: "static",
                          tone: e.severity === "actionable" ? "danger" : "info",
                        }}
                      />
                    </span>
                    <span className={styles.exceptionDetail}>{e.detail}</span>
                    <span className={styles.exceptionMeta}>
                      {t("dashboard.exceptions.task", { id: e.taskId })} ·{" "}
                      {e.selector} · {formatAge(e.ageMinutes)}
                    </span>
                  </div>
                  <Button
                    kind="text-only"
                    variant="secondary"
                    text={
                      e.kind === "suspended"
                        ? t("dashboard.exceptions.reassign")
                        : t("dashboard.exceptions.markDone")
                    }
                    isDisabled
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      }
    />
  );
}
